// @ts-check--
import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { Box, useToast } from "@chakra-ui/react";
import { Formik, Form } from "formik";

import { DEPOSIT } from "utils/constants";
import { FLOW_STATE, getFinalConfig } from "utils";
import { useFlowConfig } from "flows/hooks";

import { IframeOrFullPageWrapper } from "components/Container";
import ScrollToTop from "components/ScrollToTop";
import { FullPageSpinner } from "components/Spinner";
import Header from "flows/components/Header";
import SelectPaymentMethod from "./components/SelectPaymentMethod";
import SelectDepositType from "./components/SelectDepositType";
import Success from "./components/Success";

import { useGetAccountInfo } from "api/accounts/index.js";
import { useGetDepositCalculations, useIntentDeposit } from "api/invoicing";

const defaultConfig = {
  chooser: {
    include: ["banking", "ach"],
    exclude: [],
    addCC: false,
    addPaypal: false,
    addACH: true,
  },
};

const PAYMENT_METHOD_SELECTION = 0;
const DEPOSIT_TYPE_SELECTION = 1;
const SUCCESS = 2;

// fields that the final API will take
const defaultInitialValues = {
  // values we need to get
  accountId: null,
  requestAmount: null,
  depositPaymentMethodId: null,
  depositType: null,
  destinationWalletId: null,
  requestAmountFee: null,
  // hard coded values for now
  depositProvider: "banking",
  requestCurrency: "USD",
  // helper
  paymentMethod: null,
};

/**
 * Deposit
 * @description
 * Deposit flow
 * @param {object} props
 * @param {Config} props.config
 * @param {OnReady} props.onReady
 * @param {OnUpdate} props.onUpdate
 * @param {OnComplete} props.onComplete
 * @param {OnError} props.onError
 */
export default function Deposit({ config = {}, onReady, onComplete }) {
  useEffect(onReady, [onReady]);
  const { formatMessage } = useIntl();

  const { chooser: chooserConfig } = getFinalConfig(DEPOSIT, defaultConfig, config);

  const { hideExitButtons } = useFlowConfig();
  const toast = useToast();
  const [curScreen, setCurScreen] = useState(PAYMENT_METHOD_SELECTION);
  const [initialValues, setInitialValues] = useState(defaultInitialValues);

  const {
    data: accountInfo,
    isPending: isGetAccountInfoLoading,
    isSuccess: isGetAccountInfoSuccess,
    error: accountInfoError,
  } = useGetAccountInfo();

  const {
    mutateAsync: getDepositCalculations,
    data: depositCalculations,
    isSuccess: isGettingDepositCalculationsSuccess,
    isPending: isLoadingDepositCalculations,
    error: depositCalculationsError,
  } = useGetDepositCalculations();

  const {
    mutateAsync: intentDeposit,
    isSuccess: isIntentDepositSuccess,
    isPending: isLoadingIntentDeposit,
    error: intentDepositError,
  } = useIntentDeposit();

  const handleCloseButtonClicked = () => {
    onComplete({
      state: curScreen === SUCCESS ? FLOW_STATE.COMPLETE : FLOW_STATE.CANCEL,
      trigger: "header-close-button",
    });
  };

  const goNextScreen = () => {
    setCurScreen(curScreen + 1);
  };

  const goPrevScreen = () => {
    setCurScreen(curScreen - 1);
  };

  const formatAmount = (amount) => Math.round(amount * 100);

  const handlePaymentMethodSelection = (values) => {
    const amount = formatAmount(values.requestAmount);
    getDepositCalculations({
      paymentMethodId: values.depositPaymentMethodId,
      amount,
    });
  };

  const handleDepositTypeSelection = (values) => {
    const requestAmount = formatAmount(values.requestAmount);
    intentDeposit({
      ...values,
      requestAmount,
    });
  };

  useEffect(() => {
    if (isGettingDepositCalculationsSuccess || isIntentDepositSuccess) {
      goNextScreen();
    }
  }, [isGettingDepositCalculationsSuccess, isIntentDepositSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const error = intentDepositError || depositCalculationsError || accountInfoError;

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        isClosable: true,
      });
    }
  }, [intentDepositError, depositCalculationsError, accountInfoError, toast]);

  useEffect(() => {
    if (isGetAccountInfoSuccess) {
      setInitialValues((initialValues) => ({
        ...initialValues,
        accountId: accountInfo.payload.account_id,
      }));
    }
  }, [accountInfo, isGetAccountInfoSuccess]);

  const renderByScreen = () => {
    switch (curScreen) {
      case PAYMENT_METHOD_SELECTION:
        return (
          <SelectPaymentMethod
            onSubmit={handlePaymentMethodSelection}
            chooserConfig={chooserConfig}
          />
        );
      case DEPOSIT_TYPE_SELECTION:
        return (
          <SelectDepositType
            depositOptions={depositCalculations.payload.options}
            onSubmit={handleDepositTypeSelection}
          />
        );
      case SUCCESS:
        return <Success onComplete={handleCloseButtonClicked} hideExitButtons={hideExitButtons} />;
      default:
        return null;
    }
  };

  const onBackButtonClick = curScreen === DEPOSIT_TYPE_SELECTION ? goPrevScreen : null;
  const isLoading =
    isGetAccountInfoLoading || isLoadingDepositCalculations || isLoadingIntentDeposit;

  return (
    <IframeOrFullPageWrapper includeTosAndPrivacy withoutPadding>
      <ScrollToTop>
        <Header
          showLanguage
          title={formatMessage({
            defaultMessage: "Deposit",
            description: "Money transaction type",
          })}
          onBackButtonClick={onBackButtonClick}
          onCloseButtonClick={!hideExitButtons ? handleCloseButtonClicked : undefined}
        />
        <Box mt="4" mb="4">
          <Formik initialValues={initialValues} enableReinitialize={true}>
            <Form>
              {isLoading ? <FullPageSpinner data-testid="deposit-spinner" /> : renderByScreen()}
            </Form>
          </Formik>
        </Box>
      </ScrollToTop>
    </IframeOrFullPageWrapper>
  );
}
